import { Badge, Button, Checkbox } from '@mantine/core';
import { IconCalendar, IconPencil, IconTrash } from '@tabler/icons-react';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

import { LinkedEntitiesSection } from 'src/Components/EntityLinks/LinkedEntitiesSection';
import UserAvatar from 'src/Components/NavBar/UserAvatar';
import { RowActions } from 'src/Components/Shared/RowActions';
import { SlateRenderer } from 'src/Components/Shared/SlateRenderer';
import { SlateNode } from 'src/Components/Shared/SlateRenderer/SlateRenderer.types';
import { EMPTY_DESCRIPTION } from 'src/Hooks/Forms/useCreateTaskForm';
import { LinkableEntityType } from 'src/Services/API/EntityLinks.api';
import { getNextTaskStatus, TaskCategory, TaskStatus, TaskSubCategory } from 'src/types/task.types';

import styles from './TaskDetails.module.scss';
import { Props } from './TaskDetails.types';

const parseDescription = (description: string | null | undefined): SlateNode[] => {
  if (!description) return EMPTY_DESCRIPTION;
  try {
    const parsed = JSON.parse(description);
    if (Array.isArray(parsed)) return parsed;
    return EMPTY_DESCRIPTION;
  } catch {
    // Legacy plain text - wrap in paragraph
    return [{ type: 'paragraph', children: [{ text: description }] }];
  }
};

const getStatusBadgeColor = (status: TaskStatus) => {
  switch (status) {
    case TaskStatus.TODO:
      return 'orange';
    case TaskStatus.IN_PROGRESS:
      return 'blue';
    case TaskStatus.DONE:
      return 'teal';
    default:
      return 'gray';
  }
};

const TaskDetails = ({ task, editTask, deleteTask, onStatusChange }: Props) => {
  const { t } = useTranslation();

  const isCompleted = task.status === TaskStatus.DONE;
  const isOverdue = task.dueDate && dayjs(task.dueDate).isBefore(dayjs(), 'day') && !isCompleted;
  const isDueSoon =
    task.dueDate &&
    dayjs(task.dueDate).diff(dayjs(), 'day') <= 1 &&
    dayjs(task.dueDate).isAfter(dayjs(), 'day');

  const getStatusLabel = (status: TaskStatus) => {
    switch (status) {
      case TaskStatus.TODO:
        return t('tasks.status.todo', 'À faire');
      case TaskStatus.IN_PROGRESS:
        return t('tasks.status.inProgress', 'En cours');
      case TaskStatus.DONE:
        return t('tasks.status.done', 'Terminé');
      default:
        return '';
    }
  };

  const getCategoryLabel = (category: TaskCategory) => {
    switch (category) {
      case TaskCategory.TECHNIQUE:
        return t('tasks.category.technique', 'Technique');
      case TaskCategory.VIE_MAISON:
        return t('tasks.category.vieMaison', 'Vie de la maison');
      case TaskCategory.GESTION:
        return t('tasks.category.gestion', 'Gestion');
      default:
        return category;
    }
  };

  const getSubCategoryLabel = (subCategory: TaskSubCategory) => {
    const keyMap: Record<TaskSubCategory, string> = {
      [TaskSubCategory.PLOMBERIE]: 'plomberie',
      [TaskSubCategory.ELECTRICITE]: 'electricite',
      [TaskSubCategory.MENUISERIES]: 'menuiseries',
      [TaskSubCategory.PEINTURE_ENDUITS]: 'peintureEnduits',
      [TaskSubCategory.REPARATIONS_DIVERSES]: 'reparationsDiverses',
      [TaskSubCategory.JARDINAGE]: 'jardinage',
      [TaskSubCategory.MENAGE_RANGEMENT]: 'menageRangement',
      [TaskSubCategory.DECORATION]: 'decoration',
      [TaskSubCategory.COURSES_APPROVISIONNEMENT]: 'coursesApprovisionnement',
      [TaskSubCategory.ADMINISTRATIF]: 'administratif',
      [TaskSubCategory.COMPTABILITE]: 'comptabilite',
      [TaskSubCategory.JURIDIQUE]: 'juridique',
      [TaskSubCategory.SOURCING_ACHATS]: 'sourcingAchats',
      [TaskSubCategory.GESTION_SINISTRES]: 'gestionSinistres',
      [TaskSubCategory.RELATIONS_EXTERIEURES]: 'relationsExterieures',
    };
    return t(`tasks.subCategory.${keyMap[subCategory]}`, subCategory);
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <Checkbox
          checked={isCompleted}
          size="lg"
          onClick={() => onStatusChange(task.id, isCompleted ? TaskStatus.TODO : TaskStatus.DONE)}
          readOnly
        />
        <div className={styles.headerInfo}>
          <div className={styles.title}>{task.title}</div>
          <div className={styles.badges}>
            <Badge
              size="sm"
              color={getStatusBadgeColor(task.status)}
              style={{ cursor: 'pointer' }}
              onClick={() => onStatusChange(task.id, getNextTaskStatus(task.status))}
            >
              {getStatusLabel(task.status)}
            </Badge>
            {task.category && (
              <Badge variant="light" size="sm">
                {getCategoryLabel(task.category)}
              </Badge>
            )}
            {task.subCategory && (
              <Badge variant="outline" size="sm">
                {getSubCategoryLabel(task.subCategory)}
              </Badge>
            )}
          </div>
        </div>
        <div className={styles.headerActions}>
          <RowActions
            actions={[
              {
                key: 'edit',
                label: t('common.edit', 'Modifier'),
                icon: <IconPencil size={18} />,
                onClick: editTask,
              },
              {
                key: 'delete',
                label: t('common.delete', 'Supprimer'),
                icon: <IconTrash size={18} />,
                variant: 'danger',
                onClick: () => deleteTask(task.id),
              },
            ]}
          />
        </div>
      </div>

      <div className={styles.details}>
        <div className={styles.detailSection}>
          <div className={styles.sectionTitle}>{t('tasks.details.info', 'informations')}</div>
          <div className={styles.keyValueLine}>
            <div className={styles.key}>{t('tasks.details.dueDate', 'Échéance')}</div>
            <div
              className={`${styles.value} ${isOverdue ? styles.overdue : ''} ${isDueSoon ? styles.dueSoon : ''}`}
            >
              {task.dueDate ? (
                <>
                  <IconCalendar size={14} />
                  <span>{dayjs(task.dueDate).format('DD/MM/YYYY')}</span>
                </>
              ) : (
                '-'
              )}
            </div>
          </div>
          <div className={styles.keyValueLine}>
            <div className={styles.key}>{t('tasks.details.assignedTo', 'Assigné à')}</div>
            <div className={styles.value}>
              {task.assignedTo ? (
                <>
                  <UserAvatar user={task.assignedTo} size="sm" />
                  <span>{`${task.assignedTo.firstName} ${task.assignedTo.lastName}`}</span>
                </>
              ) : (
                '-'
              )}
            </div>
          </div>
          <div className={styles.keyValueLine}>
            <div className={styles.key}>{t('tasks.details.createdBy', 'Créé par')}</div>
            <div className={styles.value}>
              <UserAvatar user={task.createdBy} size="sm" />
              <span>{`${task.createdBy.firstName} ${task.createdBy.lastName}`}</span>
            </div>
          </div>
          {task.timeSpentMinutes != null && task.timeSpentMinutes > 0 && (
            <div className={styles.keyValueLine}>
              <div className={styles.key}>{t('tasks.details.timeSpent', 'Temps passé')}</div>
              <div className={styles.value}>
                {task.timeSpentMinutes >= 60
                  ? `${Math.floor(task.timeSpentMinutes / 60)}h${task.timeSpentMinutes % 60 > 0 ? ` ${task.timeSpentMinutes % 60}min` : ''}`
                  : `${task.timeSpentMinutes}min`}
              </div>
            </div>
          )}
          <div className={styles.keyValueLine}>
            <div className={styles.key}>{t('tasks.details.createdAt', 'Créé le')}</div>
            <div className={styles.value}>{dayjs(task.createdAt).format('DD/MM/YYYY')}</div>
          </div>
        </div>

        {task.description && (
          <div className={styles.detailSection}>
            <div className={styles.sectionTitle}>
              {t('tasks.details.description', 'description')}
            </div>
            <div className={styles.description}>
              <SlateRenderer value={parseDescription(task.description)} />
            </div>
          </div>
        )}
      </div>

      <LinkedEntitiesSection
        entityType={LinkableEntityType.Task}
        entityId={task.id}
        entityTitle={task.title}
      />

      <div className={styles.border} />
      <div className={styles.buttons}>
        <Button onClick={() => deleteTask(task.id)} variant="danger">
          {t('common.delete', 'Supprimer')}
        </Button>
        <Button onClick={editTask} variant="outline">
          {t('common.edit', 'Modifier')}
        </Button>
      </div>
    </div>
  );
};

export default TaskDetails;
